import { Page } from "./Page"
import { throttle } from "lodash"

export type TimeSeriesPageRequest = {
	pageId: number
	studyId: string
	patientId: string
	modalities: string[]
	startDate: Date | number
	endDate: Date | number
	numPoints: number
	resolution: string
}

export class TimeSeriesPageQueue<PageType extends Page<any>> {
	public loadQueue: PageType[] = []
	public unloadQueue: PageType[] = []
	public onSuccess = (page: PageType) => {}
	public onPageLoadStart = (page: PageType) => {}
	public allLoadedPages = new Map<number, PageType>()

	public activePageRequest: PageType | null = null

	push = (pages: PageType[]) => {
		const nonDuplicates = pages.filter(page => !page.loading && !this.loadQueue.find(p => page.id === p.id))
		this.loadQueue.push(...nonDuplicates)
		this.pop()
	}

	unloadAllLoadedPages() {
		this.allLoadedPages.forEach(page => page.unload())
	}

	unloadOutside = throttle((neighborhood: PageType[]) => {
		const neighborhoodPages = new Set(neighborhood)

		this.unloadQueue.length = 0

		// Iterate over the keys of the Map
		for (const page of this.allLoadedPages.values()) {
			if (!neighborhoodPages.has(page)) {
				this.unloadQueue.push(page)
			}
		}

		// Delete the keys outside the loop to avoid modifying the Map while iterating
		for (const page of this.unloadQueue) {
			this.allLoadedPages.delete(page.id)
			page.unload()
		}
	}, 500)

	pop = () => {
		if (this.activePageRequest?.needsToBeLoaded()) {
			// Then the page request is not actually loading, and needs to be re-requested, or cancelled.
			this.loadQueue.unshift(this.activePageRequest)
			this.activePageRequest = null
		}

		if (this.loadQueue.length === 0 || this.activePageRequest) {
			return
		}

		const pageRequest = this.loadQueue.shift()

		if (!pageRequest) {
			return
		}

		this.activePageRequest = pageRequest

		// Ensure partial loads trigger re-rendering.
		pageRequest.setOnPartialLoad((loadedPage: Page<any>) => {
			this.onSuccess(loadedPage as PageType)
			this.allLoadedPages.set(loadedPage.id, loadedPage as PageType)
		})

		// When the page fully loads, call onSuccess and also remove the page request from the queue.
		pageRequest.setOnFinishedLoading((loadedPage: Page<any>) => {
			// A page whose first response completes it never fires onPartialLoad, so
			// without this it would never be tracked here, and never be unloaded.
			this.allLoadedPages.set(loadedPage.id, loadedPage as PageType)
			this.onSuccess(loadedPage as PageType)
			this.activePageRequest = null
			this.pop()
		})
	
		// If the page doesn't need to be loaded for some reason, explicitly handle that early exit case.
		// This is to make sure we aren't calling the expensive "onSuccess" with the expectation that new data has arrived.
		pageRequest.setOnLoadEarlyExit(() => {
			this.activePageRequest = null
			this.pop()
		})

		// When the page fully loads, call onSuccess and also remove the page request from the queue.
		try {
			pageRequest.load()
			this.onPageLoadStart(pageRequest)
		} catch (error) {
			console.error(error)
			pageRequest.unload()
			this.activePageRequest = null
			this.loadQueue.unshift(pageRequest)
			this.pop()
		}
	}

	cancel = () => {
		if (this.activePageRequest) {
			this.activePageRequest.cancel()
			this.activePageRequest = null
		}
		this.clear()
	}

	clear = () => {
		this.loadQueue.length = 0
	}
}
